/**
 * Batas waktu bawaan untuk permintaan BACA (GET) tanpa timeout, Okt 2026 (Fase 5).
 *
 * Masalah: sebagian besar pemanggilan data di aplikasi tidak punya batas waktu.
 * Di sinyal lemah, layar bisa loading tanpa akhir tanpa pesan apa pun.
 *
 * Aturan:
 *  - Hanya GET yang belum menetapkan timeout sendiri (yang sudah menetapkan tidak diubah).
 *  - Endpoint laporan/ekspor/rekap memakai batas lebih longgar (120 detik).
 *  - Unduhan/berkas besar (responseType blob/arraybuffer/stream/text besar) dilewati.
 *  - Permintaan tulis (POST/PUT/PATCH/DELETE) tidak disentuh: timeout pada tulis bisa
 *    berarti data sudah masuk tapi balasan hilang, jadi dibiarkan seperti semula.
 *  - Saat gagal karena sinyal (tanpa respons/timeout), tampil satu info singkat
 *    (dibatasi sekali per 60 detik dan hanya saat tab terlihat).
 */

import axios from 'axios';
import { toast } from 'sonner';

export const DEFAULT_GET_TIMEOUT_MS = 30000;
// Endpoint laporan/ekspor/rekap memang bisa berat di server: diberi batas lebih longgar
// supaya yang dulu akhirnya berhasil tidak jadi terpotong.
export const HEAVY_GET_TIMEOUT_MS = 120000;
const HEAVY_URL = /export|excel|download|report|backup|rekap|recap|scorecard|payroll|audit-logs|pph|analytic|statistic|\/stats|summary|chats\/all|ocr|preview|auto-po|recommendation|sop-audit|supplier-bundle|tab-data/i;
const TOAST_GAP_MS = 60000;
const TOAST_ID = 'weak-signal-read';

let lastToastAt = 0;
let installed = null;

const isBulky = (config) => {
  if (!config) return false;
  const rt = config.responseType;
  return !!rt && rt !== 'json';
};

export function installRequestTimeout() {
  if (installed) return installed;

  const reqId = axios.interceptors.request.use((config) => {
    const method = String(config.method || 'get').toLowerCase();
    if (method === 'get' && !config.timeout && !isBulky(config)) {
      config.timeout = HEAVY_URL.test(String(config.url || '')) ? HEAVY_GET_TIMEOUT_MS : DEFAULT_GET_TIMEOUT_MS;
      config.__vmDefaultTimeout = true;
    }
    return config;
  });

  const resId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const cfg = error?.config;
        if (
          cfg?.__vmDefaultTimeout &&
          axios.isAxiosError(error) &&
          !axios.isCancel(error) &&
          error.code !== 'ERR_CANCELED' &&
          !error.response &&
          typeof document !== 'undefined' &&
          !document.hidden
        ) {
          const now = Date.now();
          if (now - lastToastAt > TOAST_GAP_MS) {
            lastToastAt = now;
            toast.info('Sinyal lemah, data belum termuat.', {
              id: TOAST_ID,
            });
          }
        }
      } catch {
        /* info tidak boleh mengubah hasil permintaan */
      }
      return Promise.reject(error);
    }
  );

  installed = () => {
    axios.interceptors.request.eject(reqId);
    axios.interceptors.response.eject(resId);
    installed = null;
  };
  return installed;
}
